import { createContext, useCallback, useContext, useRef, useState } from "react"

interface ToastItem {
	id: number
	title: string
	body?: string
	onClick?: () => void
}

interface ToastApi {
	/** Show a transient toast; auto-dismisses. */
	show: (toast: Omit<ToastItem, "id">) => void
}

const ToastCtx = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
	const ctx = useContext(ToastCtx)
	if (!ctx) throw new Error("useToast must be used within a ToastProvider")
	return ctx
}

const AUTO_DISMISS_MS = 6000

/**
 * Minimal app-wide toast system (ZPL-147): a context + a fixed bottom-right
 * stack. No external dependency. Toasts auto-dismiss; clicking a toast runs its
 * `onClick` then dismisses.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = useState<ToastItem[]>([])
	const nextId = useRef(1)

	const dismiss = useCallback((id: number) => {
		setToasts((ts) => ts.filter((t) => t.id !== id))
	}, [])

	const show = useCallback(
		(toast: Omit<ToastItem, "id">) => {
			const id = nextId.current++
			setToasts((ts) => [...ts, { ...toast, id }])
			window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
		},
		[dismiss],
	)

	return (
		<ToastCtx.Provider value={{ show }}>
			{children}
			{/* Below the fixed h-20 GlobalHeader. Mobile: full-width across the
			    top. Desktop (sm+): a fixed-width stack in the top-right corner. */}
			<div
				aria-live="polite"
				className="fixed top-24 right-0 left-0 z-40 flex flex-col gap-2 px-3 sm:left-auto sm:w-80 sm:px-0 sm:right-4"
			>
				{toasts.map((t) => (
					<ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
				))}
			</div>
		</ToastCtx.Provider>
	)
}

function ToastCard({
	toast,
	onDismiss,
}: {
	toast: ToastItem
	onDismiss: () => void
}) {
	return (
		<div className="flex items-start gap-2 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-lg">
			<button
				type="button"
				onClick={() => {
					toast.onClick?.()
					onDismiss()
				}}
				className={
					toast.onClick
						? "min-w-0 flex-1 cursor-pointer text-left"
						: "min-w-0 flex-1 text-left"
				}
			>
				<div className="truncate text-sm font-semibold text-foreground">
					{toast.title}
				</div>
				{toast.body ? (
					<div className="line-clamp-2 text-xs text-muted-foreground [overflow-wrap:anywhere]">
						{toast.body}
					</div>
				) : null}
			</button>
			<button
				type="button"
				aria-label="Close"
				onClick={onDismiss}
				className="shrink-0 px-1 text-muted-foreground hover:text-foreground"
			>
				×
			</button>
		</div>
	)
}
